"use client";

import { Empty, Table } from 'antd';
import { LoadingLottie } from '../loading';
import { Icon } from '../Icon';
import { ospThemes } from '../ospThemes';
import { InfiniteScrollDetector } from '../infiniteScroll';
import { useCssStyle, useStyles } from './styles';

import { pagination as DefaultPagination } from './pagination';
import type { TableViewProps } from './types';

/**
 * TableView component
 * @description A wrapper around antd Table with customizable background colors
 */
export function TableView<T extends Record<string, any>>(props: TableViewProps<T>) {
  const {
    columnStyle,
    cellStyle,
    spaceStyle,
    borderConfig: borderConfigProps,
    pagination: resetPagination,
    className,
    loading,
    enableInfiniteScroll = false,
    onLoadMore,
    loadingMore = false,
    hasMore = true,
    loadMoreThreshold = 100,
    loadingMoreContent,
    noMoreContent,
    ...restProps
  } = props;
  const borderConfig = { borderColor: ospThemes.colors.border_solid, borderWidth: 1, borderRadiusSize: 10, ...borderConfigProps }
  const cssStyles = useCssStyle({
    columnStyle,
    cellStyle,
    spaceStyle,
    borderConfig,
  })
  const { styles, cx } = useStyles({ cellStyle: cssStyles.cellStyle, borderConfig, columnStyle: cssStyles.columnStyle });

  return (
    <Table<T>
      loading={loading ? {
        wrapperClassName: styles.loadingWrapper,
        spinning: true,
        indicator: <div style={{ transform: 'translate(-28px, -15px)' }}>
          <LoadingLottie
            style={{
              width: 80,
              height: 80,
            }}
          />
        </div>,
      } : false}
      className={cx(styles.table, className)}
      bordered={false}
      locale={{
        emptyText: loading ? null : <Empty styles={{
          root: {
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: "column",
          },
          image: {
            alignSelf: 'center',
          },

        }} image={<Icon name="empty-result" size={120} />} description={
          <span className="title_l text-content_primary">No Data</span>
        } />,
      }}
      pagination={resetPagination ? { // 兼容resetPagination = false情况
        ...DefaultPagination,
        ...resetPagination,
      } : resetPagination}
      footer={enableInfiniteScroll ? () => (
        <InfiniteScrollDetector
          onLoadMore={onLoadMore}
          loadingMore={loadingMore}
          hasMore={hasMore}
          threshold={loadMoreThreshold}
          loadingContent={loadingMoreContent || <div style={{ transform: 'translate(0, 0)' }}>
            <LoadingLottie
              style={{
                width: 80,
                height: 80,
              }}
            />
          </div>}
          noMoreContent={noMoreContent}
        />
      ) : undefined}
      {...restProps}
    />
  );
}